import React, { useEffect, useState, useMemo } from 'react';
import { IListingData } from '@models/listing';
import StaticLayout from '@components/layout/StaticLayout';
import { useLazyQuery, useQuery } from '@apollo/client';
import { getTaxationList, filterQuery } from './queries';
import { dataToLovs, mapToListingData } from './utils';
import { filterSectionsContent, headers } from './content';
import ListingFilterWidget from '@components/widgets/listing-filter-widget/ListingFilterWidget';
import { IListingFilterWidgetSection } from '@components/widgets/listing-filter-widget';
import { IAbstractRecord } from '@models';
import { getFilter, setFilter } from '@utils/filter-utils';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import { toast } from 'react-toastify';
import { DEFAULT_ERROR_TEXT } from '@utils';
import TaxationDrawer from './taxation-drawer/TaxationDrawer';
import { isEqual } from 'lodash';
import { isEmpty } from '@utils/validationUtils';
import ListingWidget from '@components/widgets/listing-widget/ListingWidget';
import { IFilterModel } from '@components/widgets/listing-widget';
import { ActionTarget, IEnhancedMenuItem } from '@components/table/types';
import { capitalizeFirstCharacter } from '@utils/formatting-utils';
import DateService from '@services/DateService';

const FILTER_SESSION_KEY = 'taxationsFilter';

const TaxationsPage: React.FC = () => {
  const [taxationDrawerOpen, setTaxationDrawerOpen] = useState<boolean>(false);
  const [chosenEditId, setChosenEditId] = useState<string>('');
  const initialNamedFilters =
    Object.keys(getFilter(FILTER_SESSION_KEY)).length > 0
      ? getFilter(FILTER_SESSION_KEY)
      : {
          line: [],
          date: null,
          createdOn: [],
        };

  const [filterValues, setFilterValues] = useState<IFilterModel>({
    namedFilters: initialNamedFilters,
    pagination: {
      pageSize: 10,
      pageNumber: 1,
    },
  });

  const [filterSections, setFilterSections] =
    useState<IListingFilterWidgetSection[]>();
  const [tableData, setTableData] = useState<IListingData>({
    pagedItems: {},
    pageSize: 10,
    pageNumber: 1,
    totalCount: 0,
  });

  const [getTaxationsLazy, { loading: loadingTable }] =
    useLazyQuery(getTaxationList);
  const filterResponse = useQuery(filterQuery);

  const actions: IEnhancedMenuItem[] = useMemo(
    () => [
      {
        name: 'new',
        title: '+ New',
        onClick: () => {
          setChosenEditId('');
          setTaxationDrawerOpen(true);
        },
      },
      {
        name: 'modify',
        title: 'Modify',
        target: ActionTarget.Entity,
        rowIndependent: false,
        onClick: (selectedItems) => {
          if (selectedItems.length === 1 && selectedItems[0]) {
            setChosenEditId(selectedItems[0]);
            setTaxationDrawerOpen(true);
          }
        },
      },
    ],
    []
  );

  const initialize = async () => {
    try {
      const { data, error } = await filterResponse.refetch();
      if (error) {
        toast.error(
          <ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>
        );
        return;
      }
      const savedFilters =
        Object.keys(getFilter(FILTER_SESSION_KEY)).length > 0
          ? getFilter(FILTER_SESSION_KEY)
          : filterValues.namedFilters;
      const lovs = dataToLovs(data);
      const newFilterSections = filterSectionsContent(lovs, savedFilters);
      setFilterSections(newFilterSections);
      const updatedFilterValues: IFilterModel = {
        ...filterValues,
        namedFilters: savedFilters,
      };
      setFilterValues(updatedFilterValues);
      await getListingData(updatedFilterValues);
    } catch (err) {
      toast.error(<ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>);
    }
  };

  useEffect(() => {
    initialize();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const getListingData = async (filter: IFilterModel) => {
    const filterV = filter.namedFilters;
    const pagination = filter.pagination;
    try {
      const formattedDateRange = DateService.formatDateRangeBackend(
        filterV.createdOn
      );
      const { data, error } = await getTaxationsLazy({
        variables: {
          currentPage: pagination.pageNumber,
          currentPageSize: pagination.pageSize,
          selectedLines:
            filterV?.line && filterV.line.length > 0 ? filterV.line : [],
          selectedDate: DateService.formatDateBackend(filterV?.date),
          isSelectedDate: DateService.isValidDate(filterV?.date) ? true : false,
          createdDateFrom: formattedDateRange?.[0],
          createdDateTo: formattedDateRange?.[1],
          Attribute: capitalizeFirstCharacter(''),
          Descending: false,
          keywordSearch: isEmpty(filter.searchKey) ? null : filter.searchKey,
        },
      });
      if (error) {
        toast.error(
          <ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>
        );
        return;
      }
      setFilterValues(filter);
      const mappedList = mapToListingData(data);
      setTableData(mappedList);
    } catch (err) {
      toast.error(<ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>);
    }
  };

  const handlePageChange = (page: number, filterModel = filterValues) => {
    const newFilterModel = {
      ...filterModel,
      pagination: {
        ...filterModel.pagination,
        pageNumber: page,
      },
    };
    return getListingData(newFilterModel);
  };

  const onFilterUpdate = async (filters: IAbstractRecord) => {
    if (isEqual(filters, filterValues.namedFilters)) {
      return;
    }
    setFilter(filters, FILTER_SESSION_KEY);
    const newFilters: IFilterModel = {
      ...filterValues,
      namedFilters: filters,
      pagination: {
        ...filterValues.pagination,
        pageNumber: 1,
      },
    };
    setFilterValues(newFilters);
    getListingData(newFilters);
  };

  const renderFilter = () =>
    filterSections && (
      <ListingFilterWidget
        name=""
        filters={filterSections}
        onApplyFilter={onFilterUpdate}
      />
    );

  const renderMainChildren = () => (
    <>
      <ListingWidget
        title="Taxation"
        name="taxations"
        data={tableData}
        tableSettings={{ headers: headers }}
        onPageChange={(filterModel) => {
          const newFilterModel = {
            ...filterValues,
            ...filterModel,
          };
          const page = filterModel.pagination.pageNumber;
          return handlePageChange(page, newFilterModel);
        }}
        usePagination
        disableSelection={false}
        disabledSorting
        actions={actions}
        loading={loadingTable}
      />
      {taxationDrawerOpen && (
        <TaxationDrawer
          open={taxationDrawerOpen}
          onClose={() => {
            setChosenEditId('');
            setTaxationDrawerOpen(false);
          }}
          onSuccess={() => {
            setChosenEditId('');
            handlePageChange(1);
          }}
          taxationId={chosenEditId}
        />
      )}
    </>
  );

  return (
    <StaticLayout
      loading={!filterSections}
      name="Taxations"
      leftChildren={renderFilter()}
      mainChildren={renderMainChildren()}
    />
  );
};

export default TaxationsPage;
